import React from "react";
import {
  AlertIcon,
  CalendarIcon,
  CornerDownRightIcon,
  GlobeIcon,
  HeartIcon,
  LightningIcon,
  MegaphoneIcon,
  MinusCircleIcon,
  MobilePhoneIcon,
  MoneyIcon,
  PersonIcon,
  PersonWithCheckIcon,
  PinIcon,
  RefreshIcon,
  SearchIcon,
  SettingsIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  SparklesIcon,
  SunshineIcon,
  TargetIcon,
  TrendIcon,
} from "ui/icons";
import hero from "../../../../public/assets/cdp-industries/heros/retail.png";
import { dataBreachStat, personalizedExperiencesStat, revenueReferenceCopy, type CdpIndustryConfig } from "./shared";

export const retailCdpConfig: CdpIndustryConfig = {
  icon: ShoppingBagIcon,
  name: "retail",
  pluralNoun: "retailers",
  color: "violet",
  solutionPageSlug: "/solutions/retail",
  seoCopy:
    "Leverage all your retail data, including customers, products, transactions, inventory, stores, and loyalty members.",
  heroImage: {
    src: hero,
    alt: "A photograph of cosmetics with a list of example data tables overlayed. The data tables included are for Inventory, Loyalty Members, Customers, Transactions, and Promotions.",
  },
  adoptingCdpsDescription: (
    <>
      Retail has incredibly low margins and customers have more options than ever before on where and when to shop.{" "}
      {revenueReferenceCopy}
    </>
  ),
  featuredOutcomes: [
    {
      icon: TrendIcon,
      color: "green",
      title: "Grow LTV",
    },
    {
      icon: ShoppingCartIcon,
      color: "violet",
      title: "Increase purchase frequency",
    },
    {
      icon: HeartIcon,
      color: "red",
      title: "Build loyalty",
    },
  ],
  dataStats: [
    {
      title: "$37.9T",
      description: (
        <>
          Estimated
          <br />
          worldwide retail sales by 2030
        </>
      ),
      href: "https://capitaloneshopping.com/research/retail-statistics/",
    },
    personalizedExperiencesStat,
    dataBreachStat,
  ],
  personalizationCards: [
    {
      title: "Amazon is driving 35% of all purchases through personalized recommendations",
      href: "https://www.mckinsey.com/industries/retail/our-insights/how-retailers-can-keep-up-with-consumers/",
    },
    {
      title: "Walmart is generating 40% more spend through the mobile app",
      href: "https://www.retaildive.com/ex/mobilecommercedaily/walmart-app-users-spend-40-percent-more-than-average-shopper",
    },
    {
      title: "95% of all sales for PetSmart come through the company's loyalty program",
      href: "/customers/petsmart",
    },
  ],
  useCases: {
    performanceMarketing: [
      // First row
      {
        icon: PersonWithCheckIcon,
        title: "Acquisition campaigns",
        description:
          "Drive new customer acquisition using browsing data to target potential customers and untapped lookalike audiences with promotions like free shipping or first-purchase discounts, increasing sign-ups and purchases.",
      },
      {
        icon: MinusCircleIcon,
        title: "Suppression campaigns",
        description:
          "Reduce wasted ad spend by using loyalty program data to suppress frequent buyers from acquisition campaigns and reallocate budget toward lapsed or new customers, optimizing ROAS and efficiency.",
      },
      {
        icon: TargetIcon,
        title: "Retargeting campaigns",
        description:
          "Re-engage users who browsed or abandoned their cart by serving dynamic ads featuring their abandoned items and offering limited-time discounts, driving incremental purchases and recovering lost revenue.",
      },
      // Second row
      {
        icon: MoneyIcon,
        title: "Cross-sell campaigns",
        description:
          "Promote complementary products to existing customers by leveraging purchase data to serve targeted ads featuring relevant add-ons, increasing wallet share and fulfilling unmet shopping needs.",
      },
      {
        icon: MegaphoneIcon,
        title: "Referral campaigns",
        description:
          "Leverage existing customers to attract new shoppers through referral promotions that offer incentives for both the referrer and the new customer, driving cost-effective acquisition and brand advocacy.",
      },
      {
        icon: PinIcon,
        title: "Localized campaigns",
        description:
          "Increase store foot traffic by targeting users near a store with personalized ads highlighting exclusive in-store discounts, encouraging in-person visits and boosting sales.",
      },
      // Third row
      {
        icon: SunshineIcon,
        title: "Seasonal campaigns",
        description:
          "Maximize seasonal revenue by promoting holiday collections and early-bird discounts to create urgency and increase conversions during peak shopping periods.",
      },
      {
        icon: SettingsIcon,
        title: "Conversion APIs",
        description:
          "Share online and offline conversion data with ad platforms, enabling algorithms to better identify and target users with a higher likelihood of converting to improve campaign performance.",
      },
      {
        icon: GlobeIcon,
        title: "Retail media networks",
        description:
          "Monetize customer insights and first-party data by offering anonymized audience segments to brand partners for targeted advertising, generating incremental revenue through premium audience data sales.",
      },
    ],
    lifecycleMarketing: [
      // First row
      {
        icon: MoneyIcon,
        title: "Upsell & cross-sell campaigns",
        description:
          "Promote complementary products using SKU-level purchase data to recommend related items, increasing AOV and encouraging customers to buy more.",
      },
      {
        icon: RefreshIcon,
        title: "Reactivation campaigns",
        description:
          "Re-engage lapsed customers with personalized emails offering exclusive discounts to those who haven’t shopped in months, driving repeat purchases.",
      },
      {
        icon: SunshineIcon,
        title: "Seasonal campaigns",
        description:
          "Promote holiday-specific products and early-bird discounts, capturing seasonal demand and increasing sales during peak shopping periods.",
      },
      // Second row
      {
        icon: AlertIcon,
        title: "Price drop notifications",
        description:
          "Notify customers about price drops on previously viewed or favorited items, increasing conversion rates by leveraging urgency.",
      },
      {
        icon: PinIcon,
        title: "Localized promotions",
        description:
          "Use geo-targeted campaigns to promote in-store-only sales or events, driving foot traffic and boosting store-level revenue.",
      },
      {
        icon: ShoppingCartIcon,
        title: "Replenishment reminders",
        description:
          "Send timely reminders based on purchase frequency to encourage repeat purchases of consumable items, driving recurring revenue.",
      },
      // Third row
      {
        icon: HeartIcon,
        title: "Loyalty program engagement",
        description:
          "Offer double points, exclusive discounts, or early access to products to drive program participation and increase repeat purchases.",
      },
      {
        icon: TrendIcon,
        title: "Post-purchase nurturing",
        description:
          "Send product care guides, styling tips, or how-to videos after purchase, improving customer satisfaction and reducing returns.",
      },
      {
        icon: CalendarIcon,
        title: "Milestone celebrations",
        description:
          "Reward loyalty milestones with exclusive discounts or gifts, strengthening long-term customer relationships.",
      },
    ],
    onsitePersonalization: [
      // First row
      {
        icon: LightningIcon,
        title: "Dynamic home page content",
        description:
          "Display personalized banners and featured products based on browsing history, purchase behavior, or seasonality to increase engagement and conversions.",
      },
      {
        icon: MobilePhoneIcon,
        title: "Dynamic app experiences",
        description:
          "Customize navigation and category prioritization based on user preferences, making it easier for shoppers to find relevant products and improve retention.",
      },
      {
        icon: SearchIcon,
        title: "Dynamic search results",
        description:
          "Tailor search filters and results to highlight best-sellers, in-stock items, and relevant recommendations based on past purchases, improving search-to-purchase conversions.",
      },
      // Second row
      {
        icon: ShoppingBagIcon,
        title: "Recommended products",
        description:
          "Use purchase history and browsing data to suggest complementary items, increasing AOV through effective cross-selling.",
      },
      {
        icon: CornerDownRightIcon,
        title: "Next best action",
        description:
          "Encourage high-value customer actions such as joining a loyalty program, completing a wishlist, or adding more items to the cart to boost engagement and revenue.",
      },
      {
        icon: ShoppingCartIcon,
        title: "Proactive cart nudges",
        description:
          "Reduce cart abandonment by notifying users of low-stock items, sending reminders about items left in the cart, or offering limited-time discounts to drive conversions.",
      },
      // Third row
      {
        icon: MoneyIcon,
        title: "Savings goal tracking",
        description:
          "Show progress toward free shipping thresholds, loyalty rewards, or spending milestones to encourage additional purchases and improve customer satisfaction.",
      },
      {
        icon: PersonIcon,
        title: "Personalized rewards tracking",
        description:
          "Highlight available loyalty rewards and suggest actions to maximize benefits, increasing participation and engagement in loyalty programs.",
      },
      {
        icon: SparklesIcon,
        title: "AI-powered chat recommendations",
        description:
          "Use AI-driven chatbots to analyze user queries and browsing history in real-time, providing personalized product recommendations that improve user satisfaction and conversions.",
      },
    ],
  },
  considerations: {
    dataModeling: {
      traditional:
        "Uses predefined, generic data models that may not fully capture retail-specific nuances like SKUs or in-store data",
      composable: "Supports tailored data models to handle complex retail-specific data structures and relationships",
    },
    audienceManagement: {
      traditional:
        "Supports broad audience segmentation and targeting but struggles with retail-specific data objects and hierarchies",
      composable:
        "Enables granular segmentation leveraging any data point, including product SKUs, stores, preferences, traits, and custom data science models",
    },
    customerJourneys: {
      traditional:
        "Provides standard customer journey templates that fit common use cases like email campaigns or cart abandonment",
      composable:
        "Powers fully adaptable journeys tailored for omnichannel retail paths, including cross-channel purchase cycles",
    },
    identityResolution: {
      traditional:
        "Relies on out-of-the-box identity resolution algorithms, which may not fully integrate across retail-specific systems and use cases",
      composable:
        "Supports custom algorithms to match and unify customer profiles across diverse data sources like POS and e-commerce",
    },
  },
};
